import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';

const Privacy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-charcoal-dark text-slate-200">
      <Header />
      <Breadcrumbs />

      <main id="main-content" className="flex-grow max-w-3xl mx-auto w-full px-4 md:px-8 py-12">
        <div className="chrome-panel border border-white/5 rounded-lg p-6 md:p-8">
          <h1 className="text-3xl font-bold text-white mb-6 uppercase tracking-tight font-display">
            Privacy Policy
          </h1>

          <div className="mb-6 rounded-lg border border-urgent-orange/40 bg-urgent-orange/10 px-4 py-3 text-sm">
            <p className="text-white font-bold uppercase tracking-wide text-xs mb-1">
              Legal Disclaimer Note
            </p>
            <p className="text-slate-300 leading-relaxed">
              This Privacy Policy details our operational data handling procedures. This document should receive formal legal review by Not By Chance Inc. counsel.
            </p>
          </div>

          <div className="space-y-6 text-slate-300 text-sm leading-relaxed">
            <p><strong>Effective Date:</strong> July 18, 2026</p>
            <p><strong>Last Updated:</strong> July 19, 2026</p>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">1. Overview</h2>
              <p>
                Tent Finder is operated by Not By Chance Inc. We are committed to protecting the privacy and safety of vulnerable individuals. We collect information solely to coordinate outreach and deliver survival supplies to individuals in need.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">2. What Information We Collect</h2>
              <p>We collect the following details provided voluntarily by you:</p>
              <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                <li><strong>General Location or Landmarks:</strong> Descriptions of where you are located.</li>
                <li><strong>Precise Coordinates:</strong> Optional geographic latitude and longitude if you select a pin on our map or use &quot;Use My Location&quot;.</li>
                <li><strong>Contact Information:</strong> Optional phone numbers and email addresses for outreach follow-up.</li>
                <li><strong>Request Details:</strong> Requested items (e.g. tents, sleeping bags, water) and demographics (number of adults/children, pets present, or accessibility needs).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">3. Why We Collect It</h2>
              <p>
                We use this information strictly to coordinate volunteer outreach, deliver requested supplies, or provide referral information to other community services. Contact details are optional unless operationally necessary to coordinate access.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">4. Access Controls</h2>
              <p>
                Access to all submitted location coordinates, names, and contact details is limited exclusively to authorized Not By Chance Inc. outreach staff and system administrators. We do not expose precise location maps or directories publicly.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">5. Data Processors</h2>
              <p>
                We utilize trusted third-party processors to build, run, and secure this application:
              </p>
              <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                <li><strong>Google Firebase:</strong> Used for database storage (Firestore), authenticated administrator access, hosting files, and application security.</li>
                <li><strong>OpenStreetMap/Leaflet:</strong> Used to display maps to users for manual location pinning. Map providers process standard technical web requests when map tiles are downloaded.</li>
                <li><strong>SMTP Email Providers:</strong> Used to transmit internal admin notifications. Notification emails contain only reference numbers and non-identifying metadata, not your contact details or location descriptions.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">6. Retention and Deletion</h2>
              <p>
                We schedule closed records for deletion after a retention period (typically 30 days after closure for help requests and camp reports, and about 30 days after submission for volunteer offers). Automatic deletion requires configured Firestore TTL policies on the <code className="text-xs bg-charcoal-dark px-1 rounded">deleteAfter</code> field for:
              </p>
              <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                <li><strong>help_requests</strong> — <code className="text-xs bg-charcoal-dark px-1 rounded">deleteAfter</code> set when a request is marked resolved or closed</li>
                <li><strong>camp_reports</strong> — <code className="text-xs bg-charcoal-dark px-1 rounded">deleteAfter</code> set when a report is marked inactive</li>
                <li><strong>help_offers</strong> — <code className="text-xs bg-charcoal-dark px-1 rounded">deleteAfter</code> set when an offer is received</li>
              </ul>
              <p className="mt-3">
                Until those TTL policies are enabled in the production Firebase project, authorized administrators must remove expired records manually. We intend to delete closed request records after the stated retention period. Contact us if you would like to request deletion of information you submitted.
              </p>
              <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                <li><strong>Open requests:</strong> Maintained until resolved, closed, or manually deleted.</li>
                <li><strong>Aggregate statistics:</strong> Minimal anonymous counters (for example total closed requests by urgency or category) may be retained indefinitely for funding reports and operational planning. Aggregates do not store coordinates, names, or free-text narratives.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">7. Resource Referrals &amp; Trusted Partners</h2>
              <p>
                If we cannot directly provide an item, we may share general needs details with trusted service partners to coordinate assistance. We will never share your precise location or contact details with external groups without your explicit consent.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">8. Not Emergency Dispatch</h2>
              <p className="border-l-2 border-urgent-orange pl-3 font-semibold text-white">
                Tent Finder is NOT an emergency response service and is not monitored continuously. If you are in immediate danger or have a medical emergency, please call 911 immediately.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-white uppercase mb-2">9. Your Rights: Correction and Deletion</h2>
              <p>
                You can request immediate manual correction or deletion of your submission or contact info at any time by emailing us at{' '}
                <a href="mailto:info@notbychanceoutreach.com" className="text-banana-yellow hover:underline font-semibold">
                  info@notbychanceoutreach.com
                </a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
